import {
  DiscountApplicableOnEnumType,
  DiscountTypeEnum,
  DiscountTypeEnumType,
  GetProductDetailsResponseType
} from '@nw/shared/data-access';

interface DiscountType {
  discount_type: DiscountTypeEnumType;
  discount_amount: number;
  discount_applicable_on: DiscountApplicableOnEnumType;
  paid_customer_products_details?: {
    product_code: string;
    product_display_name: string;
  }[];
}

export abstract class BasePricingDetails {
  readonly appliedDiscounts: DiscountType[];
  readonly totalAmountToBePaid: number;
  readonly totalAmountToBePaidInLeastSubunit: number;

  constructor(productDetails: GetProductDetailsResponseType) {
    const pricingDetails =
      productDetails.product_purchase_details_for_user.pricing_details;

    this.appliedDiscounts =
      productDetails.product_purchase_details_for_user.applicable_discounts ||
      [];
    this.totalAmountToBePaid = pricingDetails.total_amount_to_be_paid || 0;
    this.totalAmountToBePaidInLeastSubunit =
      pricingDetails.total_amount_to_be_paid_in_least_subunit || 0;
  }

  get referralDiscountObject(): DiscountType | undefined {
    return this.appliedDiscounts?.find(
      (discount) => discount.discount_type === DiscountTypeEnum.enum.REFERRAL
    );
  }

  get paidCustomerDiscountObject(): DiscountType | undefined {
    return this.appliedDiscounts?.find(
      (discount) =>
        discount.discount_type === DiscountTypeEnum.enum.PAID_CUSTOMER
    );
  }

  get couponDiscountObject(): DiscountType | undefined {
    return this.appliedDiscounts?.find(
      (discount) => discount.discount_type === DiscountTypeEnum.enum.COUPON
    );
  }

  get referralDiscountValue(): number {
    return this.referralDiscountObject?.discount_amount || 0;
  }

  get paidCustomerDiscountValue(): number {
    return this.paidCustomerDiscountObject?.discount_amount || 0;
  }

  get paidCustomerDiscountProductsString(): string {
    return (
      this.paidCustomerDiscountObject?.paid_customer_products_details
        ?.map((product) => product.product_display_name)
        ?.join(',') || ''
    );
  }

  get couponDiscountValue(): number {
    return this.couponDiscountObject?.discount_amount || 0;
  }

  get paidCustomerAndRefundDiscountObjects(): DiscountType[] {
    return (
      this.appliedDiscounts?.filter(
        (discount) =>
          discount.discount_type === DiscountTypeEnum.enum.PAID_CUSTOMER ||
          discount.discount_type === DiscountTypeEnum.enum.REFUND_PRODUCT
      ) || []
    );
  }

  get paidCustomerAndRefundDiscountValue(): number {
    return this.paidCustomerAndRefundDiscountObjects.reduce(
      (total, discount) => total + (discount.discount_amount || 0),
      0
    );
  }

  abstract get isEligibleForFreeProduct(): boolean;
}
